import React from 'react';
import { useNavigate } from 'react-router-dom';
import propTypes from 'prop-types';
import {
  ProductGrid,
  ProductCard,
  ProductImageWrap,
  ProductImage,
  ProductImageFallback,
  ProductBadge,
  ProductContent,
  ProductMeta,
  ProductCategory,
  ProductSku,
  ProductTitle,
  ProductBrand,
  ProductFeatures,
  ProductFeature,
  ProductFooter,
  ProductAvailability,
  ProductInquiryButton,
} from './EachProducts.styles';
import { FiCheck, FiImage, FiArrowRight } from 'react-icons/fi';

const EachProduct = ({ filteredProducts }) => {
  const navigate = useNavigate();
  const getProductImage = (product) => {
    // Prefer `mainImage` (SCN product model) when present
    if (typeof product?.mainImage === 'string' && product?.mainImage) {
      return product.mainImage;
    }

    if (typeof product?.image === 'string') {
      return product?.image;
    }

    if (typeof product?.imageUrl === 'string') {
      return product?.imageUrl;
    }

    if (typeof product?.primaryImage === 'string') {
      return product?.primaryImage;
    }

    if (typeof product?.primaryImage?.url === 'string') {
      return product?.primaryImage?.url;
    }

    if (Array.isArray(product?.images) && product?.images?.length > 0) {
      const firstImage = product?.images?.[0];

      return typeof firstImage === 'string'
        ? firstImage
        : firstImage?.url || firstImage?.src || '';
    }

    return '';
  };

  const getProductFeatures = (product) => {
    if (Array.isArray(product?.features)) {
      return product?.features?.filter(Boolean)?.slice(0, 3);
    }

    if (Array.isArray(product?.highlights)) {
      return product?.highlights?.filter(Boolean)?.slice(0, 3);
    }

    return [];
  };
  const handleProductInquiry = (product) => {
    const productName = product?.name || product?.title || 'a safety product';

    navigate('/contact-us', {
      state: {
        inquiryType: 'product',
        productId: product?._id || product?.id || '',
        productName,
        productSku: product?.sku || product?.manufacturerPartNumber || '',
      },
    });
  };
  return (
    <ProductGrid>
      {filteredProducts?.map((product, index) => {
        const productImage = getProductImage(product);
        const productFeatures = getProductFeatures(product);

        const productName =
          product?.name || product?.title || 'Workplace Safety Product';

        const categoryName =
          product?.category?.name || product?.category || 'Safety Products';

        const brandName =
          product?.brand?.name ||
          product?.brand ||
          product?.manufacturer?.name ||
          product?.manufacturer ||
          '';

        const productSku =
          product?.sku || product?.manufacturerPartNumber || '';

        const productKey =
          product?._id ||
          product?.id ||
          product?.sku ||
          `${productName}-${index}`;

        return (
          <ProductCard key={productKey}>
            <ProductImageWrap>
              {productImage ? (
                <ProductImage
                  src={productImage}
                  alt={product?.imageAlt || product?.altText || productName}
                  loading="lazy"
                  onError={(event) => {
                    event.currentTarget.style.display = 'none';

                    const fallback = event.currentTarget.nextElementSibling;

                    if (fallback) {
                      fallback.style.display = 'grid';
                    }
                  }}
                />
              ) : null}

              <ProductImageFallback
                $hidden={Boolean(productImage)}
                aria-hidden={Boolean(productImage)}
              >
                <FiImage />
                <span>Product image unavailable</span>
              </ProductImageFallback>

              {product?.featured && <ProductBadge>Featured</ProductBadge>}
            </ProductImageWrap>

            <ProductContent>
              <ProductMeta>
                <ProductCategory>{categoryName}</ProductCategory>

                {productSku && <ProductSku>SKU: {productSku}</ProductSku>}
              </ProductMeta>

              <ProductTitle>{productName}</ProductTitle>

              {brandName && <ProductBrand>By {brandName}</ProductBrand>}

              {productFeatures?.length > 0 && (
                <ProductFeatures>
                  {productFeatures?.map((feature, featureIndex) => (
                    <ProductFeature
                      key={`${productKey}-feature-${featureIndex}`}
                    >
                      <FiCheck />
                      <span>
                        {typeof feature === 'string'
                          ? feature
                          : feature?.label ||
                            feature?.name ||
                            feature?.value ||
                            ''}
                      </span>
                    </ProductFeature>
                  ))}
                </ProductFeatures>
              )}

              <ProductFooter>
                <ProductAvailability>
                  <span>
                    {product?.availabilityLabel ||
                      product?.availability?.label ||
                      'Availability confirmed by inquiry'}
                  </span>
                </ProductAvailability>

                <ProductInquiryButton
                  type="button"
                  onClick={() => handleProductInquiry(product)}
                >
                  Ask About Product
                  <FiArrowRight />
                </ProductInquiryButton>
              </ProductFooter>
            </ProductContent>
          </ProductCard>
        );
      })}
    </ProductGrid>
  );
};

EachProduct.propTypes = {
  filteredProducts: propTypes.arrayOf(propTypes.object).isRequired,
};

export default EachProduct;
